/**
 * useFilteredNodes Hook
 *
 * Applies filter set to nodes and returns filtered results
 */

import { useMemo, useRef } from 'react';
import { GraphDataNode } from '../../types/graph.types';
import { FilterDefinition, FilterSet, FilterResult } from '../types';
import { executeFilterSet, generateFilterCacheKey } from '../engine/filterExecutor';

/**
 * Hook return type
 */
export interface UseFilteredNodesReturn {
  /** Filtered nodes */
  nodes: GraphDataNode[];
  /** Total count before filtering */
  totalCount: number;
  /** Count after filtering */
  filteredCount: number;
  /** Execution time in ms */
  executionTimeMs: number;
  /** Whether filters are active */
  isFiltering: boolean;
}

/**
 * Hook for filtering nodes based on active filters
 *
 * Uses memoization to prevent unnecessary re-computation
 *
 * @param allNodes - All nodes to filter
 * @param filterSet - Active filters
 * @param definitions - Filter definitions
 * @returns Filtered nodes and metadata
 */
export function useFilteredNodes(
  allNodes: GraphDataNode[],
  filterSet: FilterSet,
  definitions: Record<string, FilterDefinition>
): UseFilteredNodesReturn {
  // Cache the last result to avoid recomputation when only refs change
  const cacheRef = useRef<{
    key: string;
    nodesLength: number;
    result: FilterResult;
  } | null>(null);

  const result = useMemo(() => {
    const cacheKey = generateFilterCacheKey(filterSet);

    // Check if we can use cached result
    if (
      cacheRef.current &&
      cacheRef.current.key === cacheKey &&
      cacheRef.current.nodesLength === allNodes.length
    ) {
      return cacheRef.current.result;
    }

    // Compute new result
    const newResult = executeFilterSet(allNodes, filterSet, definitions);

    // Update cache
    cacheRef.current = {
      key: cacheKey,
      nodesLength: allNodes.length,
      result: newResult,
    };

    return newResult;
  }, [allNodes, filterSet, definitions]);

  return useMemo(
    () => ({
      nodes: result.nodes,
      totalCount: result.totalCount,
      filteredCount: result.filteredCount,
      executionTimeMs: result.executionTimeMs,
      isFiltering: filterSet.length > 0,
    }),
    [result, filterSet.length]
  );
}
